import useEcho from "@lib/useEcho"
import string from "@lib/string"
import eventbus from '@app/eventbus'
import user from '@app/user'
import feature from '@app/features';
import Constants from '@app/consts'

const {joinPrivate, leaveAll} = useEcho();

// Model events are distributed when a model event is received from the model channel.
//    entityTypeId  => The id of the type .     E.g. Constants.optimit_types.relation
//    eventTypeName => The name of the entity.  E.g. "Relation" 
//    action        => The action.              E.g. "update", "create", "delete", "status". See Constants.pushmessage.actions
//    data          => The data
//    target        => The target. Mainly "entity" which are entity updates. E.g. a relation update. Free text for other target. E.g. "planboard".
//    section       => Freefield. E.g. for configuration employeelist of the planboard: "employeelist"
//
class clsModelEvent {
    id_company    = null;
    entityTypeId  = null;
    eventTypeName = null;
    action        = null;
    data          = [];
    target        = null;
    section       = null;

    // An entity model specifly is target to an entity and contains all the data 
    // necesary to load/refresh the entity. 
    // This is different from a targeted event which aims a specific section of the applicaton, e.g. the planboard, 
    // and even more specific, e,g. the employeelist in the planboard.
    //  
    isEntityModel() {
        return (!!this.target) || this.target == 'entity';
    }

    isEntityType(typeId) {
        return this.entityTypeId == typeId;
    }
    isActionCreate() {
        return this.action == Constants.pushmessage.actions.CREATE;
    }
    isActionUpdate() {
        return this.action == Constants.pushmessage.actions.UPDATE;
    }
    isActionDelete() {
        return this.action == Constants.pushmessage.actions.DELETE;
    }
    isActionStatus() {
        return this.action == Constants.pushmessage.actions.STATUS;
    }
    
    firstData() {
        return this.data?.[0] || null;
    }
    get cntModels() { return this?.data?.length||0; }

    constructor(event) {
        this.id_company    = event.id_company;
        this.entityTypeId  = event.entityTypeId;
        this.eventTypeName = event.eventTypeName;
        this.action        = event.action;
        this.subAction     = event.subAction;
        this.target        = event.target;
        this.section       = event.section;

        if (Array.isArray(event.data)) {
            this.data = event.data; 
        }
        else if (event.data) {
            this.data = [event.data];
        } else {
            this.data = [];
        }
    }
}

// The frontend receives events via the echo channels. 
// This handler is resposible for subscribing to the right channels, and to distribute the events in a 'uniform' way to 
// the rest of the application.
// 
// Usage: in the App main component:  
//  import echoHandler from '@app/echoHandler'
//  onMount:   echoHandler.init();
//  onUnmount: echoHandler.destruct();
// 
class clsEchoHandler {
    dbg = false;

    log(a,b,c,d,e) {
        if (this.dbg) {
            console.log(a,b,c,d,e);
        }
    }

    /**
     * Initialize the echo handler. 
     * - register for app events
     * - on appropriate app events, unregister rom - or register to the appropriate echo channels.
     */
    init(bDbg) {
        this.dbg = !!bDbg;

        if (!feature.canHandlePushMessages) {
            this.log('Disabled support for push messages.');
            return;
        }

        eventbus.auth.loggedOut.on(  (data) => {
            this.cleanup();
        });
        eventbus.appdata.loaded.on(  (data) => {
            // Unregister all events. 
            this.cleanup();
            // Register the events again for the current configuration. 
            this._register();
        });
    }

    // cleanup - just unregister from all channels.
    cleanup() {
        leaveAll();
    }

    // Distribute the push message event to the rest of the frontend application. 
    // 1. Distribute the event as - is. Any component can listen to the push messages. 
    // 2. When appropriate, pre-process the event and send it to the correct eventbus channel. 
    //    For example, in Optimit we have meta data in list format. 
    //    Think about lists of employees, disputes, contracttypes, relations, etcetera.
    //    Now, those lists already are subscribed to update events, which are received from the 
    //    corresponding edit actions. 
    //    We don't want to have a similar subscription for lists for push messages, as the data is the same. 
    //    Therefore, we distribute those pushmessages also directly to the list channels. 
    // 
    _distributeModelChangedEvent(objEvent) {

        if (!objEvent.isEntityModel()) {
            return; // Only push entity updates to the model changed eventbus channel.
        }
        if (!(objEvent.isActionUpdate() || objEvent.isActionCreate() || objEvent.isActionDelete())) {
            return; // not an update/create/delete event, so we are not interested in distributing it to the lists.
        }
        // Model creation | update
        if (objEvent.isActionUpdate() || objEvent.isActionCreate()) {
            // as the event contains a collection of model data, we send a saved event for each model in the collection.
            objEvent.data.forEach(modelData => {
                eventbus.model.saved(string.lower(objEvent.eventTypeName), modelData);
            });

        // Model deletion
        } else if (objEvent.isActionDelete()) {
            objEvent.data.forEach(modelData => {
                eventbus.model.deleted(string.lower(objEvent.eventTypeName), modelData);
            });
        }
    }

    _distributeEvent(objEvent) {
        // Give any component the change to listen to any push message.
        eventbus.pushmessage(objEvent);
        // And when appropriate, convert the pushmessage to a model saved event and distribute it to the model eventbus channels.
        this._distributeModelChangedEvent(objEvent); 
    }

    // All our register actions.
    _register() {
        var id_company = user.activeAdministrationId;
        this.log('echoHandler: registering channels for ', id_company);
        if (!id_company) {
            this.log('echoHandler: no active administration, not registering channels');
            return;
        }
        joinPrivate(`model-events.${id_company}`).listenToAll((event, data) => {
            var objEvent = new clsModelEvent(data);
            if (objEvent.id_company != id_company) {
                return; // Should not occur due to 
            }
            this._distributeEvent(objEvent);});
    }
}

let echoHandler = null;
export default function useEchoHandler(bDbg) {
    if (!echoHandler) {
        echoHandler = new clsEchoHandler(bDbg);         
    }
    return echoHandler;
}

